import { ApolloCache } from '@apollo/client';

import {
  ConversationThreadPageDocument,
  ConversationThreadPageQuery,
} from 'core/graphql';

/**
 * Flip the per-message triage flags of ONE cached thread (spec 0030).
 *
 * Two callers need this and neither can get it from a mutation payload:
 * `useConversations.markRead` (whole-thread read) and `useBulkTriage` with
 * `scope: 'conversation'`. Without it the badges go out while the bubbles in
 * the open thread keep their unread styling and «не обработано» pills — the
 * screen contradicts itself until something refetches.
 *
 * Patching beats refetching here: the thread is already in the cache and we
 * know exactly what the server just did to it, so re-pulling 30 messages to
 * change two booleans would be pure waste.
 *
 * `relayStylePagination` keys `conversationThread` by `conversationId` alone,
 * so `first`/`after` are irrelevant to this lookup.
 */
/**
 * Дописать только что отправленное сообщение в кэш открытого треда (ТЗ 0033).
 *
 * Раньше после ответа тред РЕФЕТЧИЛСЯ первой страницей — и в
 * `@apollo/client@3.3.6` (`utilities/policies/pagination.js`) merge на входящей
 * странице без `after` обнуляет накопленный префикс. То есть менеджер, пять раз
 * нажавший «показать более ранние» и прочитавший 75 сообщений, после ответа
 * получал тред, схлопнутый обратно до 15, и кнопку догрузки заново.
 *
 * Сервер вернул нам ровно тот узел, который добавил, — дописываем его сами.
 * Заодно исчезает лишний round-trip после каждого ответа.
 *
 * Лента newest-first (разворачивает её панель), поэтому новое сообщение —
 * В ГОЛОВУ списка. `pageInfo` не трогаем: он про СТАРЫЕ сообщения.
 */
export const appendThreadMessage = (
  cache: ApolloCache<unknown>,
  conversationId: string,
  message: { id: string } & Record<string, unknown>,
): void => {
  let cached: ConversationThreadPageQuery | null = null;
  try {
    cached = cache.readQuery<ConversationThreadPageQuery>({
      query: ConversationThreadPageDocument,
      variables: { conversationId },
    });
  } catch {
    return; // тред не открывали — дописывать некуда
  }
  const thread = cached?.conversationThread;
  if (!thread) return;
  const edges = thread.edges || [];
  // Идемпотентность: сокет/поллинг мог принести это же сообщение раньше нас.
  if (edges.some((e) => e?.node?.id === message.id)) return;
  // ТЗ 0112: документ треда несёт `kind` сообщения, а узлы из ответов
  // мутаций — нет. Узел без поля документа делает запись неполной, и
  // следующий `readQuery` треда вернул бы null (тред пропал бы до рефетча).
  // Вид у всех сообщений треда один — берём его у соседей.
  const kind =
    message.kind ?? edges.find((e) => e?.node?.kind)?.node?.kind ?? null;
  cache.writeQuery({
    query: ConversationThreadPageDocument,
    variables: { conversationId },
    data: {
      ...cached,
      conversationThread: {
        ...thread,
        edges: [
          { __typename: 'MessageEdge', cursor: '', node: { ...message, kind } },
          ...edges,
        ],
      },
    } as ConversationThreadPageQuery,
  });
};

export const patchThreadTriage = (
  cache: ApolloCache<unknown>,
  conversationId: string,
  { read, handled }: { read?: boolean; handled?: boolean },
): void => {
  let cached: ConversationThreadPageQuery | null = null;
  try {
    cached = cache.readQuery<ConversationThreadPageQuery>({
      query: ConversationThreadPageDocument,
      variables: { conversationId },
    });
  } catch {
    // Thread not in cache (never opened) — nothing on screen to correct.
    return;
  }
  (cached?.conversationThread?.edges || []).forEach((edge) => {
    const node = edge?.node;
    if (!node) return;
    const fields: Record<string, () => boolean> = {};
    if (read !== undefined) fields.isRead = () => read;
    // Handled is an INBOUND-only concept — the server never sets it on our
    // own messages, so neither may the cache patch, or an outbound bubble
    // would render as «обработано» and then flip back on the next fetch.
    if (handled !== undefined && node.direction === 'inbound') {
      fields.isHandled = () => handled;
    }
    if (Object.keys(fields).length === 0) return;
    cache.modify({
      id: cache.identify({ __typename: 'MessageType', id: node.id }),
      fields,
    });
  });
};

/** Что каскад вернул в ответе мутации (`setMessageHandled`, ответ в диалог). */
export interface ICascadeTriagePayload {
  readMessageIds?: Array<string | number> | null;
  handledMessageIds?: Array<string | number> | null;
  truncated?: boolean | null;
}

/**
 * Перекрасить пузыри, которые каскад перевёл в «прочитано» / «обработано»
 * (spec 0030), — по id из ответа, без сети. Общий код тика
 * (`useMessageTriage`) и ответа в диалог (`useConversations.reply`): ответ
 * гасит всё, что пришло до него, ровно как каскадный тик.
 *
 * `truncated` — списки неполные; патчить их значит оставить тред
 * наполовину зелёным. Возвращаем `false`, и вызывающий решает сам
 * (тик ждёт следующей выборки треда, ответ красит тред целиком).
 */
export const patchCascadeTriage = (
  cache: ApolloCache<unknown>,
  payload: ICascadeTriagePayload | null | undefined,
): boolean => {
  if (!payload || payload.truncated) return false;
  (payload.readMessageIds || []).forEach((id) => {
    cache.modify({
      id: cache.identify({ __typename: 'MessageType', id: String(id) }),
      fields: { isRead: () => true },
    });
  });
  (payload.handledMessageIds || []).forEach((id) => {
    cache.modify({
      id: cache.identify({ __typename: 'MessageType', id: String(id) }),
      fields: { isHandled: () => true },
    });
  });
  return true;
};
